/**
 * LeftShield Portal - Policy Runs Page
 * Route: /runs | Nav: Security > Policy Runs
 *
 * Displays policy scan runs with filtering, triggering, and detail view.
 * Auto-polls running scans every 5 seconds.
 *
 * List migrated onto the List & Grid Standard — see
 * `.github/context/list-grid-standard.context.md` and
 * `.github/context/list-grid-standard-behaviours.context.md`, following the reference
 * migration in `src/pages/ApiKeysPage.tsx`. The Trigger Run dialog is unchanged. §12746
 * Wave 2: the chevron now navigates to the GUID-routed `RunDetailPage` (`/runs/:referenceId`)
 * instead of opening a local `RunDetailDrawer` — that component has been retired.
 */
import React, { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  makeStyles,
  tokens,
  Spinner,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Input,
  Dropdown,
  Option,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Checkbox,
  Divider,
} from '@fluentui/react-components';
import {
  PlayRegular,
  DismissCircleRegular,
  ArrowSyncRegular,
  ChevronRightRegular,
  DocumentBulletListRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { runsApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';
import { PageHeader } from '../components/common/PageHeader';
import { NarratorCard } from '../components/common/NarratorCard';
import { useAuth } from '../features/authentication';
import {
  ListPageLayout,
  MasterDetailHeader,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { Crumb, ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';
import type { Run, RunStatus, RunTrigger } from '../types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getStatusColor = (status: RunStatus): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Failed':
      return 'danger';
    case 'Running':
      return 'informative';
    case 'Cancelled':
      return 'warning';
    case 'Pending':
    default:
      return 'warning';
  }
};

const formatDuration = (iso?: string): string => {
  if (!iso) return '-';
  // Basic ISO 8601 duration handling (e.g. "00:02:34" or "PT2M34S")
  if (iso.startsWith('PT')) {
    const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (m) {
      const h = m[1] ? `${m[1]}h ` : '';
      const min = m[2] ? `${m[2]}m ` : '';
      const s = m[3] ? `${m[3]}s` : '';
      return `${h}${min}${s}`.trim() || '-';
    }
  }
  return iso;
};

/** Narrow an unknown React Query error down to the shape the unauthenticated-banner check needs. */
function isUnauthorizedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { status, message } = error as { status?: number; message?: string };
  return status === 401 || (typeof message === 'string' && message.includes('MSAL'));
}

/** Roles allowed to trigger a new policy run — mirrors `RunsPortalController.Trigger` `RequireAnyRole`. */
const TRIGGER_RUN_ROLES = ['Organization.Admin', 'TenantAdmin', 'SecurityAnalyst', 'ComplianceManager'];

/** Wraps the (unpaged, limit-capped) `runsApi.getRecent` response as a `PagedResult` —
 *  `useAdaptiveGridData` then applies search/filter/sort/paging in memory (client mode), matching
 *  the pre-migration behavior where the page only ever loaded the most recent 100 runs. */
async function fetchRunsPage(params: ListQueryParams): Promise<PagedResult<Run>> {
  const items = await runsApi.getRecent(100);
  return {
    items,
    totalCount: items.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

/** Status + Trigger are encoded together into the shared `filters` URL param (§17/§19) —
 *  `field:op:value` pairs joined with `;`, same convention as a single boolean scope toggle. */
function parseRunFilters(raw: string): { status: RunStatus | ''; trigger: RunTrigger | '' } {
  let status: RunStatus | '' = '';
  let trigger: RunTrigger | '' = '';
  if (raw) {
    for (const part of raw.split(';')) {
      const [field, , value] = part.split(':');
      if (field === 'status' && value) status = value as RunStatus;
      if (field === 'trigger' && value) trigger = value as RunTrigger;
    }
  }
  return { status, trigger };
}

function serializeRunFilters(status: RunStatus | '', trigger: RunTrigger | ''): string {
  const parts: string[] = [];
  if (status) parts.push(`status:eq:${status}`);
  if (trigger) parts.push(`trigger:eq:${trigger}`);
  return parts.join(';');
}

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalL,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
});

const crumbs: Crumb[] = [{ label: 'Decision Log' }];

// ---------------------------------------------------------------------------
// TriggerRunDialog
// ---------------------------------------------------------------------------

interface TriggerRunFormState {
  targetType: string;
  targetIdentifier: string;
  targetBranch: string;
  frameworkCode: string;
  scannersEnabled: { sast: boolean; sca: boolean; secrets: boolean; iac: boolean; container: boolean };
}

const initialTriggerForm: TriggerRunFormState = {
  targetType: 'Repo',
  targetIdentifier: '',
  targetBranch: '',
  frameworkCode: '',
  scannersEnabled: { sast: true, sca: true, secrets: true, iac: false, container: false },
};

const TriggerRunDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTrigger: (form: TriggerRunFormState) => void;
  isPending: boolean;
}> = ({ open, onOpenChange, onTrigger, isPending }) => {
  const [form, setForm] = useState<TriggerRunFormState>(initialTriggerForm);

  const handleSubmit = () => {
    onTrigger(form);
  };

  const toggleScanner = (key: keyof TriggerRunFormState['scannersEnabled']) => {
    setForm((prev) => ({
      ...prev,
      scannersEnabled: { ...prev.scannersEnabled, [key]: !prev.scannersEnabled[key] },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface style={{ maxWidth: '520px' }}>
        <DialogTitle>Trigger New Run</DialogTitle>
        <DialogBody>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              <Field label="Target Type" required>
                <Dropdown
                  value={form.targetType}
                  onOptionSelect={(_, data) =>
                    setForm((prev) => ({ ...prev, targetType: data.optionValue as string }))
                  }
                >
                  <Option value="Repo">Repo</Option>
                  <Option value="Image">Container Image</Option>
                  <Option value="Environment">Environment</Option>
                </Dropdown>
              </Field>
              <Field label="Target Identifier" required hint="e.g. org/repo or image:tag">
                <Input
                  value={form.targetIdentifier}
                  onChange={(_, data) =>
                    setForm((prev) => ({ ...prev, targetIdentifier: data.value }))
                  }
                />
              </Field>
              <Field label="Branch" hint="Leave blank for default branch">
                <Input
                  value={form.targetBranch}
                  onChange={(_, data) =>
                    setForm((prev) => ({ ...prev, targetBranch: data.value }))
                  }
                />
              </Field>
              <Field label="Framework Code" required hint="e.g. SOC2, PCI-DSS, ISO27001">
                <Input
                  value={form.frameworkCode}
                  onChange={(_, data) =>
                    setForm((prev) => ({ ...prev, frameworkCode: data.value }))
                  }
                />
              </Field>

              <Divider />

              <Body1 style={{ fontWeight: 600 }}>Sensors</Body1>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingVerticalS }}>
                <Checkbox label="SAST" checked={form.scannersEnabled.sast} onChange={() => toggleScanner('sast')} />
                <Checkbox label="SCA" checked={form.scannersEnabled.sca} onChange={() => toggleScanner('sca')} />
                <Checkbox label="Secrets" checked={form.scannersEnabled.secrets} onChange={() => toggleScanner('secrets')} />
                <Checkbox label="IaC" checked={form.scannersEnabled.iac} onChange={() => toggleScanner('iac')} />
                <Checkbox label="Container" checked={form.scannersEnabled.container} onChange={() => toggleScanner('container')} />
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={<PlayRegular />}
              onClick={handleSubmit}
              disabled={!form.targetIdentifier || !form.frameworkCode || isPending}
            >
              {isPending ? 'Triggering...' : 'Trigger Run'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// PolicyRunsPage
// ---------------------------------------------------------------------------

export const PolicyRunsPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canTriggerRun = user?.roles?.some((role) => TRIGGER_RUN_ROLES.includes(role)) ?? false;

  // Dialogs
  const [isTriggerOpen, setIsTriggerOpen] = useState(false);

  // List view state (§19) — page/sort/search/filters all persist to the URL.
  const { state, setPage, setSort, setSearch, setFilters } = useListUrlState({ defaultSort: 'name:asc' });

  const { status: statusFilter, trigger: triggerFilter } = parseRunFilters(state.filters);
  const handleStatusFilterChange = (value: RunStatus | '') =>
    setFilters(serializeRunFilters(value, triggerFilter));
  const handleTriggerFilterChange = (value: RunTrigger | '') =>
    setFilters(serializeRunFilters(statusFilter, value));

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const clientPredicate = (item: Run) => {
    if (statusFilter && item.status !== statusFilter) return false;
    if (triggerFilter && item.trigger !== triggerFilter) return false;
    return true;
  };

  const grid = useAdaptiveGridData<Run>({
    queryKeyBase: ['policy-runs-grid'],
    fetchPage: fetchRunsPage,
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['name', 'targetIdentifier'],
    filtersSerialized: state.filters,
    clientPredicate,
    sortState,
  });

  const {
    items: runs,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = grid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after filter/search.
  useEffect(() => {
    if (!isLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [isLoading, state.page, totalPages, setPage]);

  // Full (unfiltered/unpaged) run set — drives the Summary Stats cards and the pre-migration
  // "auto-poll every 5s while a run is active" behavior, independent of grid pagination/filtering.
  const runsQuery = useQuery({
    queryKey: ['policyRuns'],
    queryFn: () => runsApi.getRecent(100),
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasRunning = data?.some((r) => r.status === 'Running' || r.status === 'Pending');
      return hasRunning ? 5000 : false;
    },
  });

  // Keep the grid's own data in sync with the same poll cadence used for the stats query.
  useEffect(() => {
    if (runsQuery.dataUpdatedAt) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runsQuery.dataUpdatedAt]);

  const triggerMutation = useMutation({
    mutationFn: (form: TriggerRunFormState) => {
      const scanners: string[] = [];
      if (form.scannersEnabled.sast) scanners.push('Sast');
      if (form.scannersEnabled.sca) scanners.push('Sca');
      if (form.scannersEnabled.secrets) scanners.push('Secrets');
      if (form.scannersEnabled.iac) scanners.push('Iac');
      if (form.scannersEnabled.container) scanners.push('Container');
      return runsApi.trigger({
        targetType: form.targetType,
        targetIdentifier: form.targetIdentifier,
        targetBranch: form.targetBranch || undefined,
        frameworkCode: form.frameworkCode,
        scanners,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policyRuns'] });
      queryClient.invalidateQueries({ queryKey: ['policy-runs-grid'] });
      setIsTriggerOpen(false);
    },
  });

  // Derived stats — sourced from the full run set (not the current grid page).
  const stats = useMemo(() => {
    const data = runsQuery.data;
    if (!data) return { total: 0, running: 0, completed: 0, failed: 0 };
    return {
      total: data.length,
      running: data.filter((r) => r.status === 'Running' || r.status === 'Pending').length,
      completed: data.filter((r) => r.status === 'Completed').length,
      failed: data.filter((r) => r.status === 'Failed').length,
    };
  }, [runsQuery.data]);

  // Loading state — first-ever load, before any content (stats included) can render.
  if (runsQuery.isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading policy runs..." />
      </div>
    );
  }

  // Auth error detection
  const isUnauth = isUnauthorizedError(runsQuery.error);
  if (isUnauth) {
    return (
      <div className={styles.page}>
        <PageHeader title="Decision Log" subtitle="Every enforcement decision with trigger, outcome, impact, and proof status" showAIButton={false} />
        <UnauthenticatedBanner feature="policy runs" />
      </div>
    );
  }

  const columnCount = 9; // status, name, target, trigger, framework, findings, duration, date, chevron

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={6} columns={columnCount} />;
  } else if (isError) {
    listBody = <ErrorState message="Unable to load policy runs." onRetry={refetch} />;
  } else if (runs.length === 0) {
    listBody = (
      <EmptyState
        icon={<PlayRegular />}
        title="No Decisions Recorded"
        description="System Status: No enforcement activity detected. Connect a delivery system or trigger a manual run to begin recording decisions."
        actionLabel={canTriggerRun ? 'Trigger Run' : undefined}
        onAction={canTriggerRun ? () => setIsTriggerOpen(true) : undefined}
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="Policy runs">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('name')}
                onClick={() => toggleSort('name')}
                style={{ width: '100%' }}
              >
                Name
              </TableHeaderCell>
              <TableHeaderCell>Target</TableHeaderCell>
              <TableHeaderCell>Trigger</TableHeaderCell>
              <TableHeaderCell>Framework</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'totalFindings' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('totalFindings')}
                onClick={() => toggleSort('totalFindings')}
              >
                Findings
              </TableHeaderCell>
              <TableHeaderCell>Duration</TableHeaderCell>
              <TableHeaderCell>Date</TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(1) }} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Badge appearance="filled" color={getStatusColor(item.status)}>
                    {item.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Body1 style={{ fontWeight: 500 }}>{item.name}</Body1>
                </TableCell>
                <TableCell>
                  <div>
                    <Body2>{item.targetIdentifier}</Body2>
                    {item.targetBranch && (
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{item.targetBranch}</Body2>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge appearance="outline" size="small">{item.trigger}</Badge>
                </TableCell>
                <TableCell>
                  <Badge appearance="outline" size="small">{item.frameworkCode || '—'}</Badge>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
                    {item.criticalFindings > 0 && (
                      <Badge appearance="tint" color="danger">{item.criticalFindings} C</Badge>
                    )}
                    {item.highFindings > 0 && (
                      <Badge appearance="tint" color="danger">{item.highFindings} H</Badge>
                    )}
                    <Body2>{item.totalFindings} total</Body2>
                  </div>
                </TableCell>
                <TableCell>
                  <Body2>{formatDuration(item.duration)}</Body2>
                </TableCell>
                <TableCell>
                  <Body2>{item.startedAt ? new Date(item.startedAt).toLocaleString() : '-'}</Body2>
                </TableCell>
                <TableCell>
                  <Button
                    appearance="subtle"
                    icon={<ChevronRightRegular />}
                    aria-label="View run details"
                    onClick={() => navigate(`/runs/${item.referenceId}`)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Decision Log"
        subtitle="Every enforcement decision with trigger, outcome, impact, and proof status"
      />

      {/* AI Narrator — runs insight */}
      <NarratorCard contextType="Run" contextData={runsQuery.data ? {
        totalRuns: stats.total,
        runningCount: stats.running,
        completedCount: stats.completed,
        failedCount: stats.failed,
        recentRuns: runsQuery.data.slice(0, 5).map(r => ({ name: r.name, status: r.status, totalFindings: r.totalFindings, criticalFindings: r.criticalFindings })),
      } : undefined} />

      {/* Summary Stats — non-list content, stays outside the list's scroll frame. */}
      <div className={ps.metricGrid}>
        <Card>
          <CardHeader
            image={<DocumentBulletListRegular style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }} />}
            header={<Body2>Total Runs</Body2>}
            description={<Title2>{stats.total}</Title2>}
          />
        </Card>
        <Card>
          <CardHeader
            image={<ArrowSyncRegular style={{ fontSize: '1.5rem', color: tokens.colorPaletteBlueForeground2 }} />}
            header={<Body2>Running</Body2>}
            description={
              <Title2 style={{ color: tokens.colorPaletteBlueForeground2 }}>{stats.running}</Title2>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={
              <DocumentBulletListRegular
                style={{ fontSize: '1.5rem', color: tokens.colorPaletteGreenForeground1 }}
              />
            }
            header={<Body2>Completed</Body2>}
            description={
              <Title2 style={{ color: tokens.colorPaletteGreenForeground1 }}>{stats.completed}</Title2>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={
              <DismissCircleRegular
                style={{ fontSize: '1.5rem', color: tokens.colorPaletteRedForeground1 }}
              />
            }
            header={<Body2>Failed</Body2>}
            description={
              <Title2 style={{ color: tokens.colorPaletteRedForeground1 }}>{stats.failed}</Title2>
            }
          />
        </Card>
      </div>

      {/* Policy Runs list */}
      <div className={styles.listSection}>
        <ListPageLayout
          header={<MasterDetailHeader crumbs={crumbs} busy={isFetching && !isLoading} />}
          toolbar={
            <ListToolbar
              primaryActions={
                canTriggerRun ? (
                  <Button appearance="primary" icon={<PlayRegular />} onClick={() => setIsTriggerOpen(true)}>
                    Trigger Run
                  </Button>
                ) : undefined
              }
              fkFilters={[
                <Dropdown
                  key="status"
                  placeholder="Status"
                  value={statusFilter}
                  onOptionSelect={(_, data) => handleStatusFilterChange((data.optionValue || '') as RunStatus | '')}
                  style={{ minWidth: '150px' }}
                >
                  <Option value="">All Statuses</Option>
                  <Option value="Pending">Pending</Option>
                  <Option value="Running">Running</Option>
                  <Option value="Completed">Completed</Option>
                  <Option value="Failed">Failed</Option>
                  <Option value="Cancelled">Cancelled</Option>
                </Dropdown>,
                <Dropdown
                  key="trigger"
                  placeholder="Trigger"
                  value={triggerFilter}
                  onOptionSelect={(_, data) => handleTriggerFilterChange((data.optionValue || '') as RunTrigger | '')}
                  style={{ minWidth: '150px' }}
                >
                  <Option value="">All Triggers</Option>
                  <Option value="Manual">Manual</Option>
                  <Option value="Scheduled">Scheduled</Option>
                  <Option value="PullRequest">Pull Request</Option>
                  <Option value="Push">Push</Option>
                  <Option value="Webhook">Webhook</Option>
                </Dropdown>,
              ]}
              search={state.search}
              onSearchChange={setSearch}
              searchPlaceholder="Search runs…"
              onRefresh={refetch}
              isRefreshing={isFetching}
              testIdPrefix="policy-runs"
            />
          }
          footer={
            <Pager
              page={state.page}
              totalPages={totalPages}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
              totalCount={totalCount}
              onPageChange={setPage}
              testId="policy-runs-pager"
            />
          }
        >
          {listBody}
        </ListPageLayout>
      </div>

      {/* Trigger Run Dialog */}
      <TriggerRunDialog
        open={isTriggerOpen}
        onOpenChange={setIsTriggerOpen}
        onTrigger={(form) => triggerMutation.mutate(form)}
        isPending={triggerMutation.isPending}
      />
    </div>
  );
};
